<template lang="pug">
.form-group
  label(v-if="label") {{ label }}
  .month-picker-dropdown(ref="dropdown")
    .month-picker-trigger.form-control(
      :class="{ placeholder: !displayText }"
      @click="toggle"
    )
      span {{ displayText || placeholder }}
      span.month-picker-actions
        i.la.la-times.month-picker-clear(
          v-if="value"
          @click.stop="clear"
          title="Clear"
        )
        i.la.la-calendar.month-picker-icon
    .month-picker-panel(v-show="open")
      .month-picker-grid
        .month-picker-row
          select.form-control.form-control-sm.month-picker-year(v-model="selectedYear")
            option(v-for="y in yearOptions", :key="y", :value="y") {{ y }}
          button.month-picker-clear-btn(type="button", @click="clearAndClose") Clear
        .month-picker-months
          button.month-picker-month(
            v-for="(name, i) in monthNames"
            type="button"
            :key="i"
            :class="{ active: isSelected(i) }"
            @click="selectMonth(i)"
          ) {{ name }}
</template>

<script>
export default {
  name: 'MonthPicker',
  props: {
    /** v-model: "YYYY-MM-01" or null */
    value: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Month',
    },
  },
  data() {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 1; y--) {
      years.push(y);
    }
    return {
      open: false,
      monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      yearOptions: years,
      selectedYear: currentYear,
    };
  },
  computed: {
    displayText() {
      if (!this.value || !this.value.match(/^\d{4}-\d{2}/)) return '';
      const [y, m] = this.value.split('-').map(Number);
      const name = this.monthNames[m - 1];
      return name ? `${name} ${y}` : '';
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (v && v.match(/^\d{4}-\d{2}/)) {
          const [y] = v.split('-').map(Number);
          this.selectedYear = y;
        }
      },
    },
    selectedYear(y) {
      if (!this.value || !this.value.match(/^\d{4}-(\d{2})-01/)) return;
      const m = this.value.match(/^\d{4}-(\d{2})-01/)[1];
      const next = `${y}-${m}-01`;
      if (next !== this.value) this.$emit('input', next);
    },
  },
  mounted() {
    document.addEventListener('click', this.onClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickOutside);
  },
  methods: {
    toggle() {
      this.open = !this.open;
    },
    onClickOutside(e) {
      if (this.open && this.$refs.dropdown && !this.$refs.dropdown.contains(e.target)) {
        this.open = false;
      }
    },
    /** @param {number} monthIndex 0-11 */
    isSelected(monthIndex) {
      if (!this.value || !this.value.match(/^\d{4}-\d{2}/)) return false;
      const [y, m] = this.value.split('-').map(Number);
      return y === this.selectedYear && m === monthIndex + 1;
    },
    /** @param {number} monthIndex 0-11 */
    selectMonth(monthIndex) {
      const m = String(monthIndex + 1).padStart(2, '0');
      this.$emit('input', `${this.selectedYear}-${m}-01`);
      this.open = false;
    },
    clear() {
      this.$emit('input', null);
    },
    clearAndClose() {
      this.$emit('input', null);
      this.open = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.month-picker-dropdown {
  position: relative;
}
.month-picker-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  &.placeholder span {
    color: #6c757d;
  }
}
.month-picker-icon {
  margin-left: 8px;
  opacity: 0.6;
}
.month-picker-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.month-picker-clear {
  cursor: pointer;
  opacity: 0.6;
  font-size: 1rem;
  &:hover {
    opacity: 1;
    color: #dc3545;
  }
}
.month-picker-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.month-picker-clear-btn {
  padding: 2px 8px;
  font-size: 0.8125rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  color: #6c757d;
  &:hover {
    background: #e9ecef;
    color: #495057;
  }
}
.month-picker-panel {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1050;
  min-width: 200px;
  margin-top: 4px;
  padding: 10px;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.month-picker-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.month-picker-year {
  width: 100%;
  max-width: 120px;
}
.month-picker-months {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
}
.month-picker-month {
  padding: 6px 8px;
  font-size: 0.875rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
  &.active {
    background: #569ff7;
    border-color: #569ff7;
    color: #fff;
  }
}
</style>
